import { DiscordOAuthResponse, LineOAuthResponse, OAuthResponse } from '../../common/Types';
import type { ReplicantPlatform } from '../../ReplicantPlatform';
import { ResponseError } from '../../utils/ResponseError';
import { APIError } from '../APIError';
import { DiscordClient } from '../Discord';
import { LineOAuthClient } from './LineOAuthClient';

export default class ServerReplicantOAuth {
    constructor(
        private opts: {
            platform: ReplicantPlatform;
            discordClient?: DiscordClient;
            lineOAuthClient?: LineOAuthClient;
        },
    ) {}

    fetchAccessToken(opts: { clientId?: string; code: string; redirectUri?: string }): Promise<OAuthResponse> {
        switch (this.opts.platform) {
            case 'discord':
                return this.getDiscordAccessToken(opts.code);
            case 'line':
                return this.getLineAccessToken(opts);
            default:
                throw Error(`Cannot fetch OAuth access token: platform ${this.opts.platform} is not supported`);
        }
    }

    private getDiscordAccessToken(accessCode: string): Promise<DiscordOAuthResponse> {
        if (!this.opts.discordClient) {
            throw Error('Cannot fetch OAuth access token: Discord client is not defined');
        }

        const clientId = process.env.APP_ID;
        const clientSecret = process.env.APP_SECRET;

        if (!clientId || !clientSecret) {
            throw Error('Cannot fetch OAuth access token: missing environment variables APP_ID or APP_SECRET');
        }

        return this.opts.discordClient.getAccessToken({ accessCode, clientId, clientSecret });
    }

    private async getLineAccessToken(opts: {
        clientId?: string;
        code: string;
        redirectUri?: string;
    }): Promise<LineOAuthResponse> {
        if (!this.opts.lineOAuthClient) {
            throw Error('Cannot fetch OAuth access token: LINE client is not defined');
        }

        const { code, redirectUri } = opts;

        if (!redirectUri) {
            throw Error('Cannot fetch OAuth access token: redirectUri is missing');
        }

        const { clientId, clientSecret } = getLineLoginChannel(opts.clientId);

        try {
            return await this.opts.lineOAuthClient.getAccessToken({ code, redirectUri, clientId, clientSecret });
        } catch (error) {
            if (error instanceof ResponseError && error.statusCode === 400) {
                throw APIError.withCode(error.responseBody, 'authorization_error', 'invalid_signature');
            }

            throw error;
        }
    }
}

function getLineLoginChannel(requestClientId?: string): { clientId: string; clientSecret: string } {
    const defaultId = process.env.LINE_LOGIN_CHANNEL_ID;
    const defaultSecret = process.env.LINE_LOGIN_CHANNEL_SECRET;
    const newId = process.env.NEW_LINE_LOGIN_CHANNEL_ID;
    const newSecret = process.env.NEW_LINE_LOGIN_CHANNEL_SECRET;

    if (requestClientId) {
        if (requestClientId !== defaultId && requestClientId !== newId) {
            throw Error('Unsupported clientId in OAuth request');
        }

        if (requestClientId === newId) {
            if (!newSecret) {
                throw Error('Missing environment variable NEW_LINE_LOGIN_CHANNEL_SECRET');
            }

            return { clientId: newId, clientSecret: newSecret };
        }
    }

    if (!defaultId) {
        throw Error('Missing environment variable LINE_LOGIN_CHANNEL_ID');
    }

    if (!defaultSecret) {
        throw Error('Missing environment variable LINE_LOGIN_CHANNEL_SECRET');
    }

    return { clientId: defaultId, clientSecret: defaultSecret };
}
